import { PiaProcessFilter, TimePeriodOptions } from '../../constants'
import dayjs from 'dayjs'
import { gql } from 'graphql-request'

const getDateTimeFilter = (timePeriod: string, key = 'CREATED_AT') => {
  if (timePeriod === TimePeriodOptions.FROM_THE_START) return ''

  const mapTimePeriodToStartDate = {
    [TimePeriodOptions.ONE_MONTH]: dayjs().subtract(1, 'M').unix(),
    [TimePeriodOptions.THREE_MONTHS]: dayjs().subtract(3, 'M').unix(),
    [TimePeriodOptions.SIX_MONTHS]: dayjs().subtract(6, 'M').unix(),
    [TimePeriodOptions.ONE_YEAR]: dayjs().subtract(1, 'y').unix()
  }

  return `{ key: ${key}, start: ${mapTimePeriodToStartDate[timePeriod]}, end: ${dayjs().unix()} }`
}

export const fetchPrivacyOpsConfigStatus = () => {
  return gql`
    {
      dataSubjectRequestForm {
        count
      }
      preferenceCenter {
        count
      }
      consentManagementSettings {
        edges {
          node {
            datasource {
              count
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }
      piaTotal: process(booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]) {
        count
      }
      ropaTotal: ropaProcess(booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]) {
        count
      }
    }
  `
}

export const mapPrivacyOpsConfigStatus = (raw: any) => {
  const isPrivacyOpsConfigured =
    raw?.dataSubjectRequestForm?.count > 0 ||
    raw?.preferenceCenter?.count > 0 ||
    raw?.consentManagementSettings?.edges?.reduce((sum: number, edge: any) => {
      return sum + (edge?.node?.datasource?.count || 0)
    }, 0) > 0 ||
    raw?.piaTotal?.count > 0 ||
    raw?.ropaTotal?.count > 0
  return {
    isPrivacyOpsConfigured: isPrivacyOpsConfigured || false
  }
}

export const queryDsrSummary = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      dsrTotal: dsrRequest${createdDateFilter ? `(dateFilter: [${createdDateFilter}])` : ''} {
        count
      }
      dsrOpen: dsrRequest(
        filter: [{ key: STATUS, values: ["idVerified", "idUnverified", "dataDiscovery", "reportGeneration"] }]
        ${createdDateFilter ? `dateFilter: [${createdDateFilter}]` : ''}
      ) {
        count
      }
      dsrTotalTickets: dsrTicket${createdDateFilter ? `(dateFilter: ${createdDateFilter})` : ''} {
        count
      }
      dsrOpenTickets: dsrTicket(
        filter: [
          { key: STATUS, values: ["ticketAssigned", "inProgress"] }
        ]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      dsrNeedAction: dsrRequest(
        filter: [
          {
            key: MANUAL_ACTION
            values: ["APPROVE", "CLOSE_REQUEST", "DISCOVER_DATA_AND_VALIDATE", "SHARE_REPORT"]
          }
        ]
        ${createdDateFilter ? `dateFilter: [${createdDateFilter}]` : ''}
      ) {
        count
      }
      dsrAutomationFaliures: dsrRequest(booleanFilter: [{key: AUTOMATION_FAILURE_OCCURRED, value: true}], dateFilter: [${createdDateFilter}]) {
        count
      }
      dsrPastRequestDue: dsrRequest(
        dateFilter: [{ key: DUE_DATE, start: 0, end: ${dayjs().unix()} }, ${createdDateFilter}]
        filter: [{ key: STATUS, values: ["idVerified", "idUnverified", "dataDiscovery", "reportGeneration"] }]
      ) {
        count
      }
    }
  `
}

export const mapQueryDsrSummary = (raw: any) => {
  return {
    openRequests: raw.dsrOpen.count || 0,
    totalRequests: raw.dsrTotal.count || 0,
    openTickets: raw.dsrOpenTickets.count || 0,
    totalTickets: raw.dsrTotalTickets.count || 0,
    requestsNeedAttention: raw.dsrNeedAction.count || 0,
    automationFailures: raw.dsrAutomationFaliures.count || 0,
    requestsPastDue: raw.dsrPastRequestDue.count || 0
  }
}

export const queryNeedAttentionData = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      dsrNeedAction: dsrRequest(
        filter: [
          {
            key: MANUAL_ACTION
            values: ["APPROVE", "CLOSE_REQUEST", "DISCOVER_DATA_AND_VALIDATE", "SHARE_REPORT"]
          }
        ]
        ${createdDateFilter ? `dateFilter: [${createdDateFilter}]` : ''}
      ) {
        count
      }
      PIANeedReview: process(
        filter: [
          { key: PROCESS_TYPE, values: ["PIA"] }
          { key: ASSESSMENT_REVIEW, values: ["${PiaProcessFilter.inProgress}", "${
    PiaProcessFilter.notSubmitted
  }", "${PiaProcessFilter.reviewPending}"] }
        ]
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      RoPANeedReview: ropaProcess(
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        filter: [{ key: REVIEW_STATUS, values: ["Review Due", "Review Past Due"] }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
    }
  `
}

export const mapQueryNeedAttentionData = (raw: any) => {
  return {
    dsrCount: raw.dsrNeedAction.count || 0,
    piaAssessmentCount: raw.PIANeedReview.count || 0,
    ropaProcessCount: raw.RoPANeedReview.count || 0
  }
}

export const queryRopaSummary = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  const processCreatedDateFilter = getDateTimeFilter(timePeriod, 'PROCESS_CREATED_AT')
  return gql`
    {
      ropaProcessesTotal: ropaProcess(
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      ropaProcessesNeedingReview: ropaProcess(
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        filter: [{ key: REVIEW_STATUS, values: ["Review Due", "Review Past Due"] }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      unmappedEnabledAttributes: attribute(
        filter: [{ key: UNMAPPED_ATTRIBUTES_BY_PROCESS_TYPE, values: ["ROPA"] }]
        ${processCreatedDateFilter ? `dateFilter: [${processCreatedDateFilter}]` : ''}
      ) {
        count
      }
    }
  `
}

export const mapQueryRopaSummary = (raw: any) => {
  return {
    totalProcesses: raw.ropaProcessesTotal.count || 0,
    processesNeedAttention: raw.ropaProcessesNeedingReview.count || 0,
    unmappedAttributeTypes: raw.unmappedEnabledAttributes.count || 0
  }
}

export const queryDsrTypesSummary = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      inProgressDsrRequests: dsrRequest(
        filter: [{ key: STATUS, values: ["idVerified", "dataDiscovery", "reportGeneration"] }]
        ${createdDateFilter ? `dateFilter: [${createdDateFilter}]` : ''}
      ) {
        count
      }
      awaitingApprovalDsrRequests: dsrRequest(
        filter: [{ key: STATUS, values: ["idUnverified"] }]
        ${createdDateFilter ? `dateFilter: [${createdDateFilter}]` : ''}
      ) {
        count
      }
      closedDsrRequests: dsrRequest(
        filter: [{ key: STATUS, values: ["closed", "rejected"] }]
        ${createdDateFilter ? `dateFilter: [${createdDateFilter}]` : ''}
      ) {
        count
      }
      dsrReportGroupByRequestType {
        edges {
          node {
            requestType
            dsrRequest${createdDateFilter ? `(dateFilter: [${createdDateFilter}])` : ''} {
              count
            }
          }
        }
      }
    }
  `
}

export const mapQueryDsrTypesSummary = (raw: any) => {
  return {
    list: raw.dsrReportGroupByRequestType.edges.map(({ node }) => ({
      type: node.requestType,
      count: node.dsrRequest.count
    })),
    counts: {
      inProgress: raw.inProgressDsrRequests.count,
      awaitingApproval: raw.awaitingApprovalDsrRequests.count,
      closed: raw.closedDsrRequests.count
    }
  }
}

export const queryRisksBasedOnGroups = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      piaProcessesTotal: process(
        filter: [
          { key: PROCESS_TYPE, values: ["PIA"] }
          { key: ASSESSMENT_REVIEW, values: ["${PiaProcessFilter.reviewed}"] }
        ]
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      riskProcessGroup: processGroup(
        filter: [
          { key: PROCESS_TYPE, values: ["PIA"] }
          { key: ASSESSMENT_REVIEW, values: ["${PiaProcessFilter.reviewed}"] }
        ]
        toggleFilter: { key: ALL_RISK }
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        edges {
          node {
            processGroupId
            processGroupName
            count
            inReviewCount
          }
        }
      }
    }
  `
}

export const mapQueryRisksBasedOnGroups = (raw: any) => {
  return {
    total: raw.piaProcessesTotal.count,
    list: raw.riskProcessGroup.edges.map(({ node }) => ({
      id: node.processGroupId,
      name: node.processGroupName,
      count: node.count
    }))
  }
}

export const queryProcessBasedOnGroups = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      ropaProcessesTotal: ropaProcess(
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      processGroup(
        filter: [{ key: PROCESS_TYPE, values: ["ROPA"] }]
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
        edges {
          node {
            processGroupId
            processGroupName
            count
            inReviewCount
          }
        }
      }
    }
  `
}

export const mapQueryProcessBasedOnGroups = (raw: any) => {
  return {
    total: raw.ropaProcessesTotal.count,
    list: raw.processGroup.edges.map(({ node }) => ({
      id: node.processGroupId,
      name: node.processGroupName,
      count: node.count
    }))
  }
}

export const queryPiaAssessmentsBasedOnRiskReviews = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      riskScale(type: RISK) {
        edges {
          node {
            id
            displayName
            colour
            process(
              filter: [
                { key: PROCESS_TYPE, values: ["PIA"] }
                { key: ASSESSMENT_REVIEW, values: ["${PiaProcessFilter.reviewed}"] }
              ]
              booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
              ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
            ) {
              count
            }
          }
        }
      }
    }
  `
}

export const mapQueryPiaAssessmentsBasedOnRiskReviews = (raw: any) => {
  return raw.riskScale.edges.map(({ node }) => ({
    id: node.id,
    riskType: node.displayName,
    count: node.process.count,
    color: node.colour
  }))
}

export const queryPiaSummary = (timePeriod: string) => {
  const createdDateFilter = getDateTimeFilter(timePeriod)
  return gql`
    {
      assessmentCount: process(
        filter: [{ key: PROCESS_TYPE, values: ["PIA"] }]
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
      assessmentPendingReview: process(
        filter: [
          { key: PROCESS_TYPE, values: ["PIA"] }
          { key: ASSESSMENT_REVIEW, values: ["${PiaProcessFilter.inProgress}", "${
    PiaProcessFilter.notSubmitted
  }", "${PiaProcessFilter.reviewPending}"] }
        ]
        booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]
        ${createdDateFilter ? `dateFilter: ${createdDateFilter}` : ''}
      ) {
        count
      }
    }
  `
}

export const mapQueryPiaSummary = (raw: any) => {
  return {
    assessmentsCount: raw.assessmentCount.count,
    pendingReviewAssessmentsCount: raw.assessmentPendingReview.count
  }
}
